import { watch, nextTick, shallowRef, reactive, toRefs, type Ref, computed } from 'vue'
import type { Action, DirectoryItem, Header } from '../types'
import IconFolderSmall from '@/design-system/assets/icons/IconFolderSmall.vue'
import IconFileSmall from '@/design-system/assets/icons/IconFileSmall.vue'
import IconLinkSmall from '@/design-system/assets/icons/IconLinkSmall.vue'

export interface GridOptions {
  getGridBodyHeight: () => number
  isGridFocused: () => boolean
  scrollToRow: (id: string) => void
  setDragImage: (dataTransfer: DataTransfer) => void
  focusGrid: () => void
}

type GridProps = {
  directoryItems: DirectoryItem[]
  headers?: Header[]
  actions?: Action[]
  maxActions?: number
  currentItemId?: string
  dragDropEnabled?: boolean
  multiSelectEnabled?: boolean
  showFooter?: boolean
}
type GridRefs = { [K in keyof GridProps]: Ref<GridProps[K]> }
type GridEmits = {
  (e: 'select', items: DirectoryItem[]): void
  (e: 'doubleClick', item: DirectoryItem): void
  (e: 'action', action: string, items: DirectoryItem[]): void
  (e: 'sort', headers: Header[]): void
  (e: 'itemsDroppedOnTarget', payload: { draggedItemIds: string[]; targetItemId: string }): void
}

export function useDirectoryGrid(props: GridRefs, emit: GridEmits, options: GridOptions) {
  const {
    directoryItems,
    headers,
    maxActions,
    currentItemId,
    dragDropEnabled,
    multiSelectEnabled
  } = props

  const state = reactive({
    hoveredItem: undefined as DirectoryItem | undefined,
    selectedItemIds: [] as string[],
    localHeaders: headers?.value ?? [],
    focusedRowIndex: -1,
    gridHasFocus: false,
    isKeyboardNavigating: false,
    lastSelectedRowIndex: -1,
    isDragging: false,
    currentlyDraggedItemIds: [] as string[],
    dropTargetItemId: undefined as string | undefined,
    dragPreviewName: '',
    dragPreviewIconComponent: shallowRef<any>(),
    dragPreviewCount: 0,
    get activeRowId() {
      if (state.focusedRowIndex >= 0 && state.focusedRowIndex < directoryItems.value.length) {
        return `row-${directoryItems.value[state.focusedRowIndex].id}`
      }
      return undefined
    },
    get columnsTemplate() {
      const actionsColumnWidth = `${(maxActions?.value ?? 3) * 45 + 16}px`
      return [
        ...(state.localHeaders ?? []).map((header) => header.columnWidth ?? '1fr'),
        actionsColumnWidth
      ].join(' ')
    }
  })

  const previewItemSelected = computed(() => {
    return (
      state.selectedItemIds.length === 1 &&
      directoryItems.value.find((item) => item.id === state.selectedItemIds[0])?.type === 'file'
    )
  })

  const removeEntityConnectionHeader = computed(() => {
    return (
      props.showFooter?.value &&
      previewItemSelected.value &&
      headers?.value?.some((header) => header.key === 'entityConnection')
    )
  })

  const computeSelectedItems = () =>
    state.selectedItemIds
      .map((id) => directoryItems.value.find((item) => item.id === id))
      .filter((item): item is DirectoryItem => item !== undefined)

  const getIconComponentByType = (type: DirectoryItem['type']) => {
    switch (type) {
      case 'directory':
        return IconFolderSmall
      case 'file':
        return IconFileSmall
      case 'link':
        return IconLinkSmall
      default:
        return IconFileSmall
    }
  }

  const selectRange = (startIndex: number, endIndex: number): string[] => {
    const min = Math.min(startIndex, endIndex)
    const max = Math.max(startIndex, endIndex)
    return directoryItems.value.slice(min, max + 1).map((item) => item.id)
  }

  if (currentItemId) {
    watch(
      currentItemId,
      (newId) => {
        if (newId) {
          const newIndex = directoryItems.value.findIndex((item) => item.id === newId)
          if (newIndex !== -1) {
            state.focusedRowIndex = newIndex
            state.lastSelectedRowIndex = newIndex
            state.selectedItemIds = [newId]
            emit('select', computeSelectedItems())
          }
        } else {
          state.selectedItemIds = []
          state.focusedRowIndex = -1
          state.lastSelectedRowIndex = -1
          emit('select', [])
        }
      },
      { immediate: true }
    )
  }

  watch(
    [previewItemSelected, props.showFooter],
    ([isSelected, showingFooter]) => {
      if (isSelected && removeEntityConnectionHeader.value && showingFooter) {
        state.localHeaders = state.localHeaders.filter(
          (header) => header.key !== 'entityConnection'
        )
      } else if (headers?.value) {
        state.localHeaders = headers.value.map((h) => ({
          ...h,
          sortDirection: h.sortDirection ?? undefined
        }))
      }
    },
    { immediate: true, deep: true }
  )

  if (headers) {
    watch(
      headers,
      (newHeaders) => {
        state.localHeaders = (newHeaders ?? []).map((h) => ({
          ...h,
          sortDirection: h.sortDirection ?? undefined
        }))
      },
      { immediate: true, deep: true }
    )
  }

  const onGridFocus = () => {
    if (state.focusedRowIndex === -1 && directoryItems.value.length > 0) {
      state.focusedRowIndex = 0
    }
  }

  const handleComponentFocusIn = () => {
    state.gridHasFocus = true
  }
  const handleComponentFocusOut = () => {
    setTimeout(() => {
      if (!options.isGridFocused()) {
        state.gridHasFocus = false
        state.isKeyboardNavigating = false
      }
    }, 0)
  }

  const _navigate = (newIndex: number, event?: KeyboardEvent) => {
    state.isKeyboardNavigating = true
    if (newIndex === state.focusedRowIndex) return
    state.focusedRowIndex = newIndex

    if (event?.shiftKey && multiSelectEnabled?.value) {
      const anchor =
        state.lastSelectedRowIndex === -1 ? state.focusedRowIndex || 0 : state.lastSelectedRowIndex
      state.selectedItemIds = selectRange(anchor, newIndex)
      emit('select', computeSelectedItems())
    } else if (!event?.ctrlKey) {
      state.selectedItemIds = [directoryItems.value[newIndex].id]
      state.lastSelectedRowIndex = newIndex
      emit('select', computeSelectedItems())
    }
    nextTick(() => state.activeRowId && options.scrollToRow(state.activeRowId))
  }

  const navigateRows = (direction: 1 | -1, event?: KeyboardEvent) => {
    if (directoryItems.value.length === 0) return
    let newIndex = state.focusedRowIndex + direction
    if (state.focusedRowIndex === -1)
      newIndex = direction === 1 ? 0 : directoryItems.value.length - 1
    newIndex = Math.max(0, Math.min(directoryItems.value.length - 1, newIndex))
    _navigate(newIndex, event)
  }

  const navigateToEdgeRow = (edge: 'first' | 'last', event?: KeyboardEvent) => {
    if (directoryItems.value.length === 0) return
    const newIndex = edge === 'first' ? 0 : directoryItems.value.length - 1
    _navigate(newIndex, event)
  }

  const navigatePage = (direction: 1 | -1, event?: KeyboardEvent) => {
    if (directoryItems.value.length === 0) return
    const ROW_HEIGHT = 40
    const itemsPerPage = Math.floor(options.getGridBodyHeight() / ROW_HEIGHT) || 1
    let newIndex = state.focusedRowIndex + itemsPerPage * direction
    newIndex = Math.max(0, Math.min(directoryItems.value.length - 1, newIndex))
    _navigate(newIndex, event)
  }

  const handleClickOnRow = (event: MouseEvent, item: DirectoryItem, index: number) => {
    state.isKeyboardNavigating = false
    if (event.detail > 1) return
    options.focusGrid()
    state.focusedRowIndex = index
    const { ctrlKey, shiftKey } = event

    if (shiftKey && multiSelectEnabled?.value) {
      const anchor = state.lastSelectedRowIndex === -1 ? index : state.lastSelectedRowIndex
      state.selectedItemIds = selectRange(anchor, index)
    } else if (ctrlKey && multiSelectEnabled?.value) {
      const newSelection = new Set(state.selectedItemIds)
      if (newSelection.has(item.id)) newSelection.delete(item.id)
      else newSelection.add(item.id)
      state.selectedItemIds = Array.from(newSelection)
      state.lastSelectedRowIndex = index
    } else {
      state.selectedItemIds = [item.id]
      state.lastSelectedRowIndex = index
    }
    emit('select', computeSelectedItems())
  }

  const handleDoubleClick = (item: DirectoryItem) => {
    state.isKeyboardNavigating = false
    emit('doubleClick', item)
  }

  const handleSpacebarOnActiveRow = (event: KeyboardEvent) => {
    const item = directoryItems.value[state.focusedRowIndex]
    if (!item) return

    const isSelected = state.selectedItemIds.includes(item.id)
    if (event.ctrlKey && multiSelectEnabled?.value) {
      if (isSelected) {
        state.selectedItemIds = state.selectedItemIds.filter((id) => id !== item.id)
      } else {
        state.selectedItemIds.push(item.id)
      }
    } else {
      if (isSelected && state.selectedItemIds.length === 1) {
        state.selectedItemIds = []
      } else {
        state.selectedItemIds = [item.id]
      }
    }
    state.lastSelectedRowIndex = state.focusedRowIndex
    emit('select', computeSelectedItems())
  }

  const handleEnterOnActiveRow = () => {
    const item = directoryItems.value[state.focusedRowIndex]
    if (item) handleDoubleClick(item)
  }

  const handleClickOnContainer = () => {
    state.isKeyboardNavigating = false
    if (state.selectedItemIds.length > 0) {
      state.selectedItemIds = []
      state.lastSelectedRowIndex = -1
      state.focusedRowIndex = -1
      emit('select', [])
    }
  }

  const handleDragStart = async (item: DirectoryItem, event: DragEvent) => {
    if (!dragDropEnabled?.value || !event.dataTransfer) return
    state.isDragging = true
    const idsToDrag = state.selectedItemIds.includes(item.id)
      ? [...state.selectedItemIds]
      : [item.id]
    state.currentlyDraggedItemIds = idsToDrag
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('application/json', JSON.stringify(idsToDrag))

    state.dragPreviewName = item.name
    state.dragPreviewIconComponent = getIconComponentByType(item.type)
    state.dragPreviewCount = idsToDrag.length

    await nextTick()
    options.setDragImage(event.dataTransfer)
  }

  const handleDragOverItem = (targetItem: DirectoryItem, event: DragEvent) => {
    if (!state.isDragging || !event.dataTransfer) return
    const isValidDropTarget =
      targetItem.type === 'directory' && !state.currentlyDraggedItemIds.includes(targetItem.id)
    if (isValidDropTarget) {
      event.dataTransfer.dropEffect = 'move'
      state.dropTargetItemId = targetItem.id
    } else {
      event.dataTransfer.dropEffect = 'none'
    }
  }

  const handleDropOnItem = (targetItem: DirectoryItem, event: DragEvent) => {
    if (!state.isDragging || !state.dropTargetItemId || !event.dataTransfer) return
    const draggedIds = JSON.parse(event.dataTransfer.getData('application/json'))
    emit('itemsDroppedOnTarget', { draggedItemIds: draggedIds, targetItemId: targetItem.id })
    handleDragEnd()
  }

  const handleDragEnd = () => {
    state.isDragging = false
    state.currentlyDraggedItemIds = []
    state.dropTargetItemId = undefined
  }

  const sortDirectionCycle = ['asc', 'desc', undefined] as const

  const handleSort = (headerToToggle: Header) => {
    if (headerToToggle.sortable === false) return
    state.localHeaders = state.localHeaders.map((h) => {
      if (h.key === headerToToggle.key) {
        const currentDirection = h.sortDirection
        const nextIndex =
          (sortDirectionCycle.indexOf(currentDirection) + 1) % sortDirectionCycle.length
        const newDirection = sortDirectionCycle[nextIndex]
        return {
          ...h,
          sortDirection: newDirection
        }
      }
      return {
        ...h,
        sortDirection: undefined // Reset other headers' sort direction
      }
    })
    emit('sort', state.localHeaders)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.target as HTMLElement)?.closest('.row-action-button, th, [role="button"]')) {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        return
      }
    }

    if (event.ctrlKey && event.key.toLowerCase() === 'a') {
      event.preventDefault()
      if (!multiSelectEnabled?.value) return
      state.selectedItemIds = directoryItems.value.map((item) => item.id)
      const lastIndex = directoryItems.value.length - 1
      if (lastIndex >= 0) {
        state.lastSelectedRowIndex = lastIndex
        state.focusedRowIndex = lastIndex
      }
      emit('select', computeSelectedItems())
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        navigateRows(1, event)
        break
      case 'ArrowUp':
        event.preventDefault()
        navigateRows(-1, event)
        break
      case 'Home':
        event.preventDefault()
        navigateToEdgeRow('first', event)
        break
      case 'End':
        event.preventDefault()
        navigateToEdgeRow('last', event)
        break
      case 'PageDown':
        event.preventDefault()
        navigatePage(1, event)
        break
      case 'PageUp':
        event.preventDefault()
        navigatePage(-1, event)
        break
      case 'Enter':
        event.preventDefault()
        handleEnterOnActiveRow()
        break
      case ' ': // Spacebar
        event.preventDefault()
        handleSpacebarOnActiveRow(event)
        break
      case 'Escape':
        event.preventDefault()
        if (state.selectedItemIds.length > 0) {
          handleClickOnContainer()
        } else {
          emit('action', 'back', [])
        }
        break
      case 'Delete':
        event.preventDefault()
        if (state.selectedItemIds.length > 0) {
          const selectedItems = computeSelectedItems()
          emit('action', 'delete', selectedItems)
          state.selectedItemIds = []
          state.focusedRowIndex = -1
          state.lastSelectedRowIndex = -1
          emit('select', [])
        }
        break
      case 'F2':
        event.preventDefault()
        if (state.focusedRowIndex >= 0 && state.focusedRowIndex < directoryItems.value.length) {
          const item = directoryItems.value[state.focusedRowIndex]
          emit('action', 'rename', [item])
        }
        break
    }
  }

  return reactive({
    ...toRefs(state),
    getAriaSort: (header: Header): 'ascending' | 'descending' | 'none' => {
      if (!header.sortDirection) return 'none'
      return header.sortDirection === 'asc' ? 'ascending' : 'descending'
    },
    onGridFocus,
    handleComponentFocusIn,
    handleComponentFocusOut,
    handleClickOnRow,
    handleDoubleClick,
    handleClickOnContainer,
    handleDragStart,
    handleDragOverItem,
    handleDropOnItem,
    handleDragEnd,
    handleDragLeaveItem: () => {
      state.dropTargetItemId = undefined
    },
    handleSort,
    handleKeyDown,
    handleMouseEnterRow: (item: DirectoryItem) => {
      state.hoveredItem = item
    },
    handleMouseLeaveRow: () => {
      state.hoveredItem = undefined
    },
    handleCloseMenu: () => options.focusGrid()
  })
}
